// Suscripción Web Push del cliente.
// Preferencia por dispositivo (desired) + device_id estable; se re-arma sola si el navegador la pierde.

import axios from 'axios';
import {
  savePushAuthToken,
  getPushDeviceId,
  getPushDesired,
  setPushDesired,
  peekPushDesiredSync,
  peekPushCategoryDesiredSync,
  getPushCategoryDesired,
  setPushCategoryDesired,
  type PushCategory,
} from './pushAuthStorage';
import { getApiBaseUrl } from '../config';
import { getToken } from './auth';

export { peekPushDesiredSync, peekPushCategoryDesiredSync };
export type { PushCategory };

const SW_READY_TIMEOUT_MS = 12000;
const SW_RETRY_DELAYS_MS = [500, 1500, 3000];

let cachedRegistration: ServiceWorkerRegistration | null = null;
let cachedVapidKey: string | null = null;
let swWarmPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let maintainPromise: Promise<boolean> | null = null;

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function serviceWorkerPaths() {
  const pwa = window.location.pathname.startsWith('/pwa/');
  return {
    script: pwa ? '/pwa/service-worker.js' : '/service-worker.js',
    scope: pwa ? '/pwa/' : '/',
  };
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function subscriptionUsesVapidKey(sub: PushSubscription, publicKey: string): boolean {
  const existing = sub.options?.applicationServerKey;
  // Algunos navegadores no exponen la clave: no forzar re-suscripción.
  if (!existing) return true;
  const expected = urlBase64ToUint8Array(publicKey);
  const buf = existing instanceof ArrayBuffer
    ? new Uint8Array(existing)
    : new Uint8Array(existing as ArrayBuffer);
  if (buf.byteLength !== expected.byteLength) return false;
  for (let i = 0; i < expected.byteLength; i++) {
    if (buf[i] !== expected[i]) return false;
  }
  return true;
}

async function getVapidPublicKey(): Promise<string> {
  if (cachedVapidKey) return cachedVapidKey;
  const { data } = await axios.get(`${getApiBaseUrl()}/push/vapid-public-key`);
  const key = data.publicKey as string;
  if (!key) throw new Error('vapid_missing');
  cachedVapidKey = key;
  return key;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Espera al SW activo o lo registra. Usa caché y ruta rápida si ya está instalado. */
async function waitForServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  if (cachedRegistration?.active) return cachedRegistration;

  const { script, scope } = serviceWorkerPaths();

  const resolveRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
    try {
      const existing = await navigator.serviceWorker.getRegistration(scope);
      if (existing?.active) {
        cachedRegistration = existing;
        return existing;
      }
    } catch {
      /* noop */
    }

    try {
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS)),
      ]);
      if (reg?.active) {
        cachedRegistration = reg;
        return reg;
      }
    } catch {
      /* intentar registro explícito */
    }

    try {
      const reg = await navigator.serviceWorker.register(script, {
        scope,
        updateViaCache: 'none',
      });
      if (reg.active) {
        cachedRegistration = reg;
        return reg;
      }
      // Esperar a que pase a active (instalación en curso).
      const worker = reg.installing || reg.waiting;
      if (worker) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          if (worker.state === 'activated') {
            done();
            return;
          }
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') done();
          });
          setTimeout(done, SW_READY_TIMEOUT_MS);
        });
      }
      if (reg.active) {
        cachedRegistration = reg;
        return reg;
      }
    } catch {
      return null;
    }
    return null;
  };

  if (!swWarmPromise) {
    swWarmPromise = resolveRegistration().finally(() => {
      swWarmPromise = null;
    });
  }
  return swWarmPromise;
}

async function waitForServiceWorkerWithRetry(): Promise<ServiceWorkerRegistration | null> {
  let reg = await waitForServiceWorkerRegistration();
  if (reg?.active) return reg;
  for (const delay of SW_RETRY_DELAYS_MS) {
    await sleep(delay);
    cachedRegistration = null;
    reg = await waitForServiceWorkerRegistration();
    if (reg?.active) return reg;
  }
  return null;
}

/** Precalienta SW + clave VAPID al abrir Automatizaciones (reduce fallos al tocar el toggle). */
export async function prefetchPushInfrastructure(): Promise<void> {
  if (!pushSupported()) return;
  try {
    await Promise.all([waitForServiceWorkerWithRetry(), getVapidPublicKey(), getPushDeviceId()]);
  } catch {
    /* noop */
  }
}

export interface PushServerStatus {
  active_count: number;
  mobile_count: number;
  desktop_count: number;
  current_endpoint_registered: boolean | null;
}

export async function getPushServerStatus(endpoint?: string): Promise<PushServerStatus | null> {
  try {
    const params = endpoint ? { endpoint } : {};
    const { data } = await axios.get(`${getApiBaseUrl()}/push/status`, {
      headers: authHeaders(),
      params,
    });
    return data as PushServerStatus;
  } catch {
    return null;
  }
}

export async function sendPushTest(): Promise<number> {
  const { data } = await axios.post(
    `${getApiBaseUrl()}/push/test`,
    {},
    { headers: authHeaders() },
  );
  return Number(data.sent || 0);
}

export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const reg = await waitForServiceWorkerWithRetry();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/** Estado del interruptor de una categoría en este dispositivo. */
export async function getPushToggleOn(category: PushCategory = 'automations'): Promise<boolean> {
  if (!pushSupported()) return false;
  if (Notification.permission === 'denied') return false;
  return getPushCategoryDesired(category);
}

/** Lee la suscripción del navegador sin crear una nueva. */
async function getBrowserSubscription(): Promise<PushSubscription | null> {
  try {
    const reg = await waitForServiceWorkerWithRetry();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Crea o renueva la suscripción del navegador. Con permiso granted no requiere gesto. */
async function ensureBrowserSubscription(options?: { forceRenew?: boolean }): Promise<PushSubscription | null> {
  const reg = await waitForServiceWorkerWithRetry();
  if (!reg) return null;
  const publicKey = await getVapidPublicKey();
  let sub = await reg.pushManager.getSubscription();

  if (sub && options?.forceRenew) {
    try { await sub.unsubscribe(); } catch { /* noop */ }
    sub = null;
  } else if (sub && !subscriptionUsesVapidKey(sub, publicKey)) {
    try { await sub.unsubscribe(); } catch { /* noop */ }
    sub = null;
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  return sub;
}

type RegisterResult = 'ok' | 'gone' | 'error';

async function currentNotifyFlags(): Promise<{
  notify_automations: boolean;
  notify_phone: boolean;
}> {
  return {
    notify_automations: await getPushCategoryDesired('automations'),
    notify_phone: await getPushCategoryDesired('phone'),
  };
}

async function putPushPrefsOnServer(): Promise<void> {
  if (!getToken()) return;
  const deviceId = await getPushDeviceId();
  const flags = await currentNotifyFlags();
  await axios.put(
    `${getApiBaseUrl()}/push/prefs`,
    { device_id: deviceId, ...flags },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } },
  );
}

async function registerSubscriptionOnServer(sub: PushSubscription): Promise<RegisterResult> {
  const token = getToken();
  if (!token) return 'error';
  await savePushAuthToken(token);
  const deviceId = await getPushDeviceId();
  const flags = await currentNotifyFlags();
  try {
    await axios.post(
      `${getApiBaseUrl()}/push/subscribe`,
      { subscription: sub.toJSON(), device_id: deviceId, ...flags },
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } },
    );
    return 'ok';
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status;
    const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    if (status === 409 || code === 'endpoint_gone') return 'gone';
    return 'error';
  }
}

async function ensureRegistered(sub: PushSubscription | null): Promise<boolean> {
  if (!sub) return false;
  let result = await registerSubscriptionOnServer(sub);
  if (result === 'ok') return true;
  // Solo renovar si el servidor dice que el canal está muerto.
  // Un error de red NO debe hacer unsubscribe: eso invalida el canal bueno (FCM/Apple)
  // y el próximo aviso llega 410 → se pierde el dispositivo.
  if (result === 'gone') {
    const fresh = await ensureBrowserSubscription({ forceRenew: true });
    if (!fresh) return false;
    result = await registerSubscriptionOnServer(fresh);
    return result === 'ok';
  }
  return false;
}

/**
 * Si el usuario quiere notificaciones en este dispositivo y hay permiso,
 * asegura suscripción local + registro en servidor (recrea si el canal murió).
 */
export async function maintainPushSubscription(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (!(await getPushDesired())) return false;
  if (!getToken()) return false;
  if (Notification.permission !== 'granted') return false;

  if (maintainPromise) return maintainPromise;
  maintainPromise = (async () => {
    try {
      const sub = await ensureBrowserSubscription();
      return await ensureRegistered(sub);
    } catch {
      return false;
    } finally {
      maintainPromise = null;
    }
  })();
  return maintainPromise;
}

/**
 * Sincroniza push con el servidor usando la suscripción que ya existe en el navegador.
 * Si hay preferencia ON y no hay sub, intenta recrearla (permiso granted).
 */
export async function syncPushSubscription(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  if (!getToken()) return false;
  try {
    if (await getPushDesired()) {
      return maintainPushSubscription();
    }
    const sub = await getBrowserSubscription();
    if (!sub) return false;
    // Suscripción huérfana (sin preferencia): registrar y adoptar como desired.
    const ok = await ensureRegistered(sub);
    if (ok) await setPushDesired(true);
    return ok;
  } catch {
    return false;
  }
}

export async function enablePush(): Promise<boolean> {
  return enablePushCategory('automations');
}

export async function enablePushCategory(category: PushCategory): Promise<boolean> {
  if (!pushSupported()) throw new Error('push_unsupported');
  if (!getToken()) throw new Error('not_logged_in');

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') throw new Error('permission_denied');

  // Preferencia primero: el switch no depende del SW lento.
  await setPushCategoryDesired(category, true);

  const sub = await ensureBrowserSubscription();
  const ok = await ensureRegistered(sub);
  if (!ok) throw new Error('server_register_failed');
  try {
    await putPushPrefsOnServer();
  } catch {
    /* el subscribe ya mandó los flags */
  }
  return true;
}

export async function disablePush(): Promise<void> {
  await setPushDesired(false);
  if (!pushSupported()) return;
  const reg = await waitForServiceWorkerWithRetry();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* noop */
  }
  if (getToken()) {
    const deviceId = await getPushDeviceId();
    await axios.delete(`${getApiBaseUrl()}/push/subscribe`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      data: { endpoint, device_id: deviceId },
    });
  }
}

export async function disablePushCategory(category: PushCategory): Promise<void> {
  await setPushCategoryDesired(category, false);
  const other: PushCategory = category === 'phone' ? 'automations' : 'phone';
  if (await getPushCategoryDesired(other)) {
    try {
      await putPushPrefsOnServer();
    } catch {
      /* si falla, el próximo maintain reintenta */
    }
    return;
  }
  await disablePush();
}

/** Al abrir la app / volver a visible: mantener push si el usuario lo eligió. */
export async function ensurePushReady(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (Notification.permission === 'denied') return false;
  if (!getToken()) return false;

  // Migración suave: permiso + sub local sin flag → adoptar como desired.
  if (!(await getPushDesired()) && Notification.permission === 'granted') {
    const existing = await getBrowserSubscription();
    if (existing) {
      await setPushDesired(true);
    }
  }

  if (!(await getPushDesired())) return false;
  if (Notification.permission !== 'granted') return false;
  return maintainPushSubscription();
}

export function pushErrorMessage(err: unknown, t: (key: string) => string): string {
  const code = err instanceof Error ? err.message : '';
  if (code === 'permission_denied') return t('automations.pushPermissionDenied');
  if (code === 'not_logged_in') return t('automations.pushLoginRequired');
  if (code === 'subscribe_failed' || code === 'push_unsupported') {
    return t('automations.pushBrowserUnsupported');
  }
  if (code === 'server_register_failed') return t('automations.pushServerError');
  return t('automations.pushToggleError');
}
